import { firstValueFrom } from "rxjs";
import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Constants } from "@constants";
import {
    ActividadDTO,
    GrupoDTO,
    IActividadDTO,
    IGrupoDTO,
    IPaginadoDTO,
    IPanelistaDTO,
    ITagDTO,
    IValidacionDTO,
    PaginadoDTO,
    PanelistaDTO,
    TagDTO,
    ValidacionDTO
} from "@DTOS";
import { EstadoPanelistaEnum, obtenerIdEstadoPanelista } from "@shared/enums";
import { map } from "rxjs/operators";

@Injectable({
    providedIn: "root"
})
export class PanelistasServiceBackend {
    constructor(protected httpClient: HttpClient) {}

    public async getPanelistas(
        pagina: number, cantidad: number, estado: string, pais: string
    ): Promise<PaginadoDTO<PanelistaDTO>> {
        let paginado: IPaginadoDTO<IPanelistaDTO>;

        let params = new HttpParams()
            .append("page", pagina.toString())
            .append("size", cantidad.toString());
        if (estado !== obtenerIdEstadoPanelista(EstadoPanelistaEnum.TODOS)) {
            params = params.append("status", estado);
        }
        if (pais != null && pais !== "") {
            params = params.append("country", pais);
        }

        if (estado === obtenerIdEstadoPanelista(EstadoPanelistaEnum.TODOS)) {
            paginado = await firstValueFrom(
                this.httpClient.get<IPaginadoDTO<IPanelistaDTO>>(
                    `${Constants.apiURL}/users`, { params }
                )
            );
        } else {
            paginado = await firstValueFrom(
                this.httpClient.get<IPaginadoDTO<IPanelistaDTO>>(
                    `${Constants.apiURL}/users`, { params }
                )
            ) ?? { results: [] };
        }

        const panelistas = paginado.results?.map(panelista => new PanelistaDTO(panelista)) ?? [];
        return new PaginadoDTO({...paginado, results: panelistas});
    }

    public async getPanelistasQuery(
        query: string, pagina: number, cantidad: number, estado: string, pais: string
    ): Promise<PaginadoDTO<PanelistaDTO>> {
        let paginado: IPaginadoDTO<IPanelistaDTO>;

        let params = new HttpParams()
            .append("page", pagina.toString())
            .append("size", cantidad.toString())
            .append("q", query);
        if (estado !== obtenerIdEstadoPanelista(EstadoPanelistaEnum.TODOS)) {
            params = params.append("status", estado);
        }
        if (pais != null && pais !== "") {
            params = params.append("country", pais);
        }

        if (estado === obtenerIdEstadoPanelista(EstadoPanelistaEnum.TODOS)) {
            paginado = await firstValueFrom(
                this.httpClient.get<IPaginadoDTO<IPanelistaDTO>>(
                    `${Constants.apiURL}/users`, { params }
                )
            );
        } else {
            paginado = await firstValueFrom(
                this.httpClient.get<IPaginadoDTO<IPanelistaDTO>>(
                    `${Constants.apiURL}/users`, { params }
                )
            );
        }

        const panelistas = paginado.results?.map(panelista => new PanelistaDTO(panelista)) ?? [];
        return new PaginadoDTO({...paginado, results: panelistas});
    }

    public async getPanelistasParam(
        param: string, pagina: number, cantidad: number
    ): Promise<PaginadoDTO<PanelistaDTO>> {
        const panelistasPaginados = await firstValueFrom(
            this.httpClient.get<IPaginadoDTO<IPanelistaDTO>>(
                `${Constants.apiURL}/users?page=${pagina}&size=${cantidad}&${param}`
            )
        );

        const panelistas = panelistasPaginados.results.map(panelista => new PanelistaDTO(panelista));
        return new PaginadoDTO({...panelistasPaginados, results: panelistas});
    }

    public async getPanelista(id: number): Promise<PanelistaDTO> {
        const response = await firstValueFrom(this.httpClient.get<IPanelistaDTO>(`${Constants.apiURL}/users/${id}`));

        return new PanelistaDTO(response);
    }

    public async getActividades(id: number): Promise<ActividadDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IActividadDTO[]>(
                `${Constants.apiURL}/users/${id}/activity`
            )
        );

        return response.map(actividadDTO => new ActividadDTO(actividadDTO));
    }

    public async getTags(id: number): Promise<TagDTO[]> {
        const response = await firstValueFrom(this.httpClient.get<ITagDTO[]>(`${Constants.apiURL}/users/${id}/properties`));

        return response.map(tag => new TagDTO(tag));
    }

    public async getValidaciones(id: number): Promise<ValidacionDTO> {
        const response = await firstValueFrom(
            this.httpClient.get<IValidacionDTO>(
                `${Constants.apiURL}/users/${id}/validation`
            )
        );

        return new ValidacionDTO(response);
    }

    public async getGrupos(id: number): Promise<GrupoDTO[]> {
        const response = await firstValueFrom(
            this.httpClient.get<IGrupoDTO[]>(
                `${Constants.apiURL}/users/${id}/campaigns`
            )
        );

        return response.map(grupo => new GrupoDTO(grupo));
    }

    public async postBlacklist(id: number, idsEmailsSimilares: number[]) {
        await firstValueFrom(
            this.httpClient.post(
                `${Constants.apiURL}/users/${id}/blacklist`, { similar_email_users: idsEmailsSimilares }
            )
        );
    }

    public async patchPanelista(id: number, panelista: PanelistaDTO): Promise<PanelistaDTO> {
        return await firstValueFrom(
            this.httpClient.patch<IPanelistaDTO>(
                `${Constants.apiURL}/users/${id}`, panelista.toJSON()
            ).pipe(
                map(response => new PanelistaDTO(response))
            )
        );
    }
}
